import type { ThreadRightPanelState } from "~/rightPanelStore";

/**
 * Keeping open cloud browsers awake across chats (CloudBrowserKeepAwake).
 *
 * A chat's browser sleeps after the server's idle timeout without real
 * operations (lib/dormancy). The provider's live view bypasses the broker, so
 * a person clicking in it would look idle: the browser on screen (panel or
 * floating player) is touched while they use it. Only the one on screen: a
 * chat in the background lets its browser sleep and frees the browser for
 * someone else (every browser held awake left new chats waiting in line), and
 * it wakes again when the person goes back to it (shouldWakeDormantBrowser).
 * A browser nobody uses for the in-use window (liveViewInUse) still sleeps.
 */

/** How often open browsers in use are touched: well inside the server's idle timeout. */
export const KEEP_AWAKE_BEAT_MS = 60_000;

/** Marks the provider's live-view frame, so the keyboard focus in it can be told. */
export const LIVE_VIEW_FRAME_ATTRIBUTE = "data-cloud-browser-live-view";

/** Whether a chat's panel is open on its browser. */
export function panelShowsBrowser(panel: ThreadRightPanelState | undefined): boolean {
  if (!panel?.isOpen) return false;
  return panel.surfaces.some((surface) => surface.kind === "preview" && surface.id === panel.activeSurfaceId);
}

/**
 * The chats whose browser to keep awake: open in the panel or the floating
 * player, and running on screen now (a chat switched away from, or a panel
 * remembered in storage, sends nothing).
 */
export function browsersToKeepAwake(input: {
  readonly panels: Readonly<Record<string, ThreadRightPanelState>>;
  readonly miniPlayers: Readonly<Record<string, unknown>>;
  readonly onScreen: (threadKey: string) => boolean;
}): string[] {
  const open = new Set<string>();
  for (const [threadKey, panel] of Object.entries(input.panels)) {
    if (panelShowsBrowser(panel)) open.add(threadKey);
  }
  for (const threadKey of Object.keys(input.miniPlayers)) open.add(threadKey);
  return [...open].filter((threadKey) => input.onScreen(threadKey)).sort();
}

/**
 * The person's use of the cloud browsers in this page: when they last showed
 * use of any (a pointer on one, input into one, a panel opening on one), and
 * which chats' browsers are running on screen now.
 */
export class BrowserEngagement {
  private signAt = 0;
  /** Views showing each chat's running browser (a panel and a player may both). */
  private readonly showing = new Map<string, number>();

  /** Use of any browser, now. */
  mark(now: number): void {
    this.signAt = Math.max(this.signAt, now);
  }

  get lastSignAt(): number {
    return this.signAt;
  }

  /**
   * A view shows a chat's running browser (panel or player); the returned
   * function says it stopped (unmounted, hidden, the browser asleep).
   */
  show(threadKey: string): () => void {
    this.showing.set(threadKey, (this.showing.get(threadKey) ?? 0) + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const left = (this.showing.get(threadKey) ?? 1) - 1;
      if (left > 0) this.showing.set(threadKey, left);
      else this.showing.delete(threadKey);
    };
  }

  onScreen(threadKey: string): boolean {
    return this.showing.has(threadKey);
  }
}

/** This page's engagement, shared by every browser view and the keep-awake loop. */
export const browserEngagement = new BrowserEngagement();
